import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Fade,
  Backdrop,
} from '@mui/material';
import {
  WarningRounded,
  CheckCircleRounded,
} from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, shadows } from '../../constants';

const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default', // 'default' or 'danger'
}) => {
  const isDanger = variant === 'danger';

  // Support both prop names:
  // - onClose: preferred (also used by MUI Dialog onClose)
  // - onCancel: legacy usage in some screens
  const handleClose = (...args) => {
    if (typeof onClose === 'function') return onClose(...args);
    if (typeof onCancel === 'function') return onCancel(...args);
    return undefined;
  };

  // Get appropriate icon based on variant
  const getIcon = () => {
    if (isDanger) {
      return <WarningRounded sx={{ fontSize: fonts.fontSize3xl }} />;
    }
    return <CheckCircleRounded sx={{ fontSize: fonts.fontSize3xl }} />;
  };

  // Get icon container background color
  const getIconBgColor = () => {
    if (isDanger) {
      return colors.errorLight;
    }
    return colors.infoLight;
  };

  // Get icon color
  const getIconColor = () => {
    return colors.textWhite;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        sx: {
          backgroundColor: colors.overlay,
          backdropFilter: 'blur(4px)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: borderRadius.modal,
          minWidth: '380px',
          maxWidth: '480px',
          boxShadow: shadows.xl,
          overflow: 'hidden',
          animation: 'slideIn 0.3s ease-out',
          '@keyframes slideIn': {
            '0%': {
              opacity: 0,
              transform: 'translateY(-20px) scale(0.95)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0) scale(1)',
            },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: 0,
          borderBottom: `1px solid ${colors.borderLight}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: spacing.md,
            paddingBottom: spacing.sm,
            background: `linear-gradient(135deg, ${colors.backgroundWhite} 0%, ${colors.backgroundLight} 100%)`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: borderRadius.full,
              backgroundColor: getIconBgColor(),
              marginBottom: spacing.sm,
              boxShadow: shadows.md,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: shadows.lg,
              },
            }}
          >
            <Box
              sx={{
                color: getIconColor(),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getIcon()}
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: fonts.fontSizeLg,
              fontWeight: fonts.weightSemiBold,
              color: colors.textPrimaryDark,
              textAlign: 'left',
              letterSpacing: fonts.letterSpacingTight,
            }}
          >
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: `${spacing.sm} ${spacing.md} ${spacing.md}`,
        }}
      >
        <Typography
          sx={{
            fontSize: fonts.fontSizeSm,
            color: colors.textSecondary,
            lineHeight: fonts.lineHeightNormal,
            textAlign: 'left',
          }}
        >
          {message}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          padding: `${spacing.sm} ${spacing.md} ${spacing.md}`,
          borderTop: `1px solid ${colors.borderLight}`,
          gap: spacing.sm,
          backgroundColor: colors.backgroundSoft,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            borderRadius: borderRadius.button,
            padding: `${spacing.xs} ${spacing.md}`,
            minWidth: '100px',
            fontSize: fonts.fontSizeSm,
            fontWeight: fonts.weightMedium,
            borderColor: colors.borderDefault,
            color: colors.textSecondary,
            backgroundColor: colors.backgroundWhite,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: colors.borderHover,
              backgroundColor: colors.backgroundSoft,
              transform: 'translateY(-1px)',
              boxShadow: shadows.sm,
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={() => {
            if (typeof onConfirm === 'function') onConfirm();
            handleClose();
          }}
          variant="contained"
          sx={{
            textTransform: 'none',
            borderRadius: borderRadius.button,
            padding: `${spacing.xs} ${spacing.md}`,
            minWidth: '100px',
            fontSize: fonts.fontSizeSm,
            fontWeight: fonts.weightSemiBold,
          background: isDanger ? colors.error : colors.primaryGradient,
            color: colors.textWhite,
            transition: 'all 0.2s ease',
          boxShadow: isDanger ? shadows.md : `0 12px 24px ${colors.primaryShadow}`,
            '&:hover': {
            background: isDanger ? colors.error : colors.primaryGradientHover,
              transform: 'translateY(-1px)',
            boxShadow: isDanger ? shadows.lg : `0 16px 32px ${colors.primaryShadow}`,
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;

